import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { 
  Copy, Bookmark, Search
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast, Toaster } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { ExportDropdown } from '../components/ExportDropdown';
import { ErrorBanner } from '../components/ErrorBanner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Workspace = () => {
  const { user, getAuthHeaders } = useAuth();
  const [outputs, setOutputs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [error, setError] = useState(null);

  const fetchOutputs = useCallback(async () => {
    setError(null);
    try {
      const savedOnly = filter === 'saved' ? '?saved_only=true' : '';
      const response = await fetch(`${API}/outputs/me${savedOnly}`, {
        headers: getAuthHeaders()
      });

      if (response.status === 401) {
        setError({ message: 'Your session has expired. Please sign in again.', isAuth: true });
        return;
      }

      if (response.ok) {
        setOutputs(await response.json());
      } else {
        setError({ message: 'Failed to load your outputs. Please try again.' });
      }
    } catch (err) {
      process.env.NODE_ENV === "development" && console.error('Error fetching outputs:', err);
      setError({ message: 'Could not connect to the server. Check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  }, [filter, getAuthHeaders]);

  useEffect(() => {
    setLoading(true);
    fetchOutputs();
  }, [fetchOutputs]);

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  const handleToggleSave = async (outputId, currentSaved) => {
    try {
      const response = await fetch(`${API}/outputs/${outputId}/save`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setOutputs(prev => prev.map(o => 
          o.id === outputId ? { ...o, is_saved: !currentSaved } : o
        ));
        toast.success(currentSaved ? 'Removed from saved' : 'Saved!');
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const filteredOutputs = outputs.filter(o => {
    if (searchTerm) {
      return o.generated_content.toLowerCase().includes(searchTerm.toLowerCase()) ||
             o.prompt_key.toLowerCase().includes(searchTerm.toLowerCase());
    }
    return true;
  });

  const formatPromptKey = (key) => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
      </div>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <ErrorBanner
          message={error.message}
          isAuthError={error.isAuth}
          onRetry={!error.isAuth ? () => { setLoading(true); fetchOutputs(); } : undefined}
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Toaster position="top-center" richColors />
      
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-foreground" data-testid="workspace-title">Your Workspace</h1>
        <p className="text-sm text-muted-foreground">All your generated content in one place</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search your outputs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-10"
            data-testid="workspace-search"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
            size="sm"
            className="text-xs"
            data-testid="filter-all"
          >
            All
          </Button>
          <Button
            variant={filter === 'saved' ? 'default' : 'outline'}
            onClick={() => setFilter('saved')}
            size="sm"
            className="text-xs"
            data-testid="filter-saved"
          >
            <Bookmark className="h-3.5 w-3.5 mr-1" />
            Saved
          </Button>
        </div>
      </div>

      {/* Outputs */}
      {filteredOutputs.length === 0 ? (
        <Card className="border-border" data-testid="workspace-empty">
          <CardContent className="py-14 text-center">
            <Bookmark className="h-16 w-16 text-teal-600 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-medium text-foreground mb-1">No outputs yet</h3>
            <p className="text-sm text-slate-500 mb-5 max-w-sm mx-auto">
              {filter === 'saved' 
                ? 'Save outputs from your path steps to see them here.'
                : 'Your first output is one step away — complete a path step to see it here.'}
            </p>
            <Link to="/dashboard">
              <Button size="sm" className="bg-creataby-gradient-btn text-white hover:opacity-95 font-semibold shadow-[0_2px_12px_rgba(14,129,117,0.25)]">Go to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredOutputs.map((output, index) => {
            const isExpanded = expandedId === output.id;
            return (
              <motion.div
                key={output.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <Card className="border-border" data-testid={`output-card-${index}`}>
                  <CardHeader className="flex flex-row items-start justify-between pb-2 gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-muted-foreground">
                        {formatPromptKey(output.prompt_key)}
                      </p>
                      <p className="text-xs text-muted-foreground/70 mt-0.5">
                        {new Date(output.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleCopy(output.generated_content)}
                        data-testid={`copy-output-${index}`}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className={`h-7 w-7 ${output.is_saved ? 'text-teal-400' : ''}`}
                        onClick={() => handleToggleSave(output.id, output.is_saved)}
                      >
                        <Bookmark className={`h-3.5 w-3.5 ${output.is_saved ? 'fill-current' : ''}`} />
                      </Button>
                      <ExportDropdown content={output.generated_content} promptKey={output.prompt_key} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className={`bg-secondary/50 rounded-lg p-3 sm:p-4 ${isExpanded ? '' : 'max-h-40'} overflow-hidden relative cursor-pointer`}
                      onClick={() => setExpandedId(isExpanded ? null : output.id)}
                    >
                      <div className="prose prose-sm prose-neutral max-w-none
                        prose-headings:text-foreground prose-headings:font-semibold
                        prose-h2:text-sm prose-h3:text-sm
                        prose-p:text-foreground/90 prose-p:text-sm prose-p:my-1
                        prose-li:text-foreground/90 prose-li:text-sm prose-li:my-0
                        prose-strong:text-foreground
                      ">
                        <ReactMarkdown>
                          {isExpanded 
                            ? output.generated_content 
                            : output.generated_content.substring(0, 400)}
                        </ReactMarkdown>
                      </div>
                      {!isExpanded && output.generated_content.length > 400 && (
                        <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-secondary/50 to-transparent" />
                      )}
                    </div>
                    {output.generated_content.length > 400 && (
                      <button 
                        onClick={() => setExpandedId(isExpanded ? null : output.id)}
                        className="text-xs text-muted-foreground hover:text-foreground mt-2"
                      >
                        {isExpanded ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
};

export default Workspace;
